import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, Building2, CalendarDays, Check, CheckCircle2, ChevronDown,
  Circle, Database, Facebook, FileText, Globe2, HelpCircle, Instagram,
  LoaderCircle, LockKeyhole, MessageCircle, MessagesSquare, RotateCcw, Search,
  Send, ShieldCheck, Sparkles, Users2,
} from "@/components/icons";
import { useStore } from "@/store/store";
import { Chip, IliaAvatar, IliaMark, SourceTag } from "@/components/askilia/atoms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { getOnboardingImportSnapshot, ONBOARDING_IMPORT_TASKS } from "@/lib/onboarding";

const SYSTEMS = [
  { id: "Medesk", descriptionKey: "admin.onboarding.system.clinicManagement", Icon: Database },
  { id: "Cliniko", descriptionKey: "admin.onboarding.system.practiceManagement", Icon: CalendarDays },
  { id: "Bitrix24", descriptionKey: "admin.onboarding.system.crmBookings", Icon: Building2 },
  { id: "1C:Enterprise", descriptionKey: "admin.onboarding.system.businessManagement", Icon: Database },
];

const CONNECTOR_CREDENTIALS = {
  Medesk: { username: "giorgi@vakeclinic.ge" },
  Cliniko: { username: "giorgi@vakeclinic.ge" },
  Bitrix24: { username: "giorgi@vakeclinic.ge" },
  "1C:Enterprise": { username: "giorgi@vakeclinic.ge" },
};

const CHANNELS = [
  { id: "ch-wa", name: "WhatsApp", descriptionKey: "admin.onboarding.channel.whatsapp", Icon: MessageCircle, handle: "+995 32 2 11 07 07" },
  { id: "ch-ig", name: "Instagram", descriptionKey: "admin.onboarding.channel.instagram", Icon: Instagram, handle: "@vakeclinic" },
  { id: "ch-me", name: "Messenger", descriptionKey: "admin.onboarding.channel.facebook", Icon: Facebook, handle: "Vake Medical Clinic" },
  { id: "ch-tg", name: "Telegram", descriptionKey: "admin.onboarding.channel.telegram", Icon: Send, handle: "@vakeclinic" },
];

const PERMISSIONS = [
  "admin.onboarding.permission.services",
  "admin.onboarding.permission.practitioners",
  "admin.onboarding.permission.locations",
  "admin.onboarding.permission.customers",
];

const IMPORT_TASK_ICONS = {
  secure: ShieldCheck,
  business: Building2,
  services: Database,
  resources: Users2,
  knowledge: Globe2,
  conversations: MessagesSquare,
};

function normalizeWebsite(value) {
  const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  const url = new URL(withProtocol);
  if (!url.hostname.includes(".")) throw new Error("Website address is incomplete");
  return `${url.protocol}//${url.hostname}${url.pathname === "/" ? "" : url.pathname.replace(/\/$/, "")}`;
}

export default function Onboarding() {
  const { state, actions } = useStore();
  const { t, localize } = useI18n();
  const nav = useNavigate();
  const onboarding = state.onboarding;
  const sourceConnected = Boolean(onboarding.sourceConnectedAt);
  const websiteConnected = Boolean(onboarding.websiteUrl);
  const hasChannel = Boolean(onboarding.connectedChannelIds?.length);
  const skippedSteps = new Set(onboarding.skippedStepIds || []);
  const websiteSkipped = skippedSteps.has("website");
  const channelSkipped = skippedSteps.has("channel");
  const sourceSkipped = skippedSteps.has("source");

  const [now, setNow] = useState(Date.now());
  const [expanded, setExpanded] = useState(() => {
    if (!websiteConnected && !websiteSkipped) return "website";
    if (!hasChannel && !channelSkipped) return "channel";
    if (!sourceConnected && !sourceSkipped) return "source";
    return null;
  });
  const [search, setSearch] = useState("");
  const [selectedSource, setSelectedSource] = useState(onboarding.source || null);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceError, setSourceError] = useState("");
  const [connectorRequestOpen, setConnectorRequestOpen] = useState(false);
  const [requestedConnectorName, setRequestedConnectorName] = useState(onboarding.requestedConnector?.name || "");
  const [requestedConnectorUrl, setRequestedConnectorUrl] = useState(onboarding.requestedConnector?.vendorUrl || "");
  const [connectorRequestError, setConnectorRequestError] = useState("");
  const [username, setUsername] = useState(CONNECTOR_CREDENTIALS.Medesk.username);
  const [password, setPassword] = useState("askilia-demo-password");
  const [websiteUrl, setWebsiteUrl] = useState(onboarding.websiteUrl || "https://vakeclinic.ge");
  const [websiteError, setWebsiteError] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [channelPickerMode, setChannelPickerMode] = useState(false);
  const [channelLoading, setChannelLoading] = useState(false);
  const sourceTimer = useRef(null);
  const channelTimer = useRef(null);

  const importSnapshot = getOnboardingImportSnapshot(onboarding, now);
  const reviewedSteps = Number(sourceConnected || sourceSkipped)
    + Number(websiteConnected || websiteSkipped)
    + Number(hasChannel || channelSkipped);
  const firstName = localize(state.user.name).split(" ")[0];
  const practitionerCount = state.resources.filter((item) => item.type === "person").length;
  const roomCount = state.resources.filter((item) => item.type === "room").length;
  const connectedChannelNames = new Set(CHANNELS
    .filter((channel) => onboarding.connectedChannelIds?.includes(channel.id))
    .map((channel) => channel.name));
  const connectedConversations = state.conversations.filter((conversation) => {
    const names = conversation.channels?.length ? conversation.channels : [conversation.channel];
    return names.some((name) => connectedChannelNames.has(name));
  });
  const connectedCustomerIds = new Set(connectedConversations.map((conversation) => conversation.customerId));
  const connectedConversationCount = connectedConversations.length;
  const openPotential = state.customers
    .filter((customer) => connectedCustomerIds.has(customer.id) && !["Booked/Won", "Lost", "Not now", "Disqualified"].includes(customer.stage))
    .reduce((sum, customer) => sum + Number(customer.value || 0), 0);
  const remainingChannels = CHANNELS.filter((channel) => !onboarding.connectedChannelIds?.includes(channel.id));

  const filteredSystems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term
      ? SYSTEMS.filter((system) => `${system.id} ${t(system.descriptionKey)}`.toLowerCase().includes(term))
      : SYSTEMS;
  }, [search, t]);

  useEffect(() => {
    if (onboarding.status === "not_started") {
      actions.updateOnboarding({ status: "in_progress", startedAt: new Date().toISOString() });
    }
  }, [actions, onboarding.status]);

  useEffect(() => {
    if (!onboarding.importStartedAt || onboarding.businessDataReadyAt) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 400);
    return () => window.clearInterval(id);
  }, [onboarding.importStartedAt, onboarding.businessDataReadyAt]);

  useEffect(() => {
    if (importSnapshot.ready && !onboarding.businessDataReadyAt) actions.finishOnboardingImport();
  }, [actions, importSnapshot.ready, onboarding.businessDataReadyAt]);

  useEffect(() => () => {
    if (sourceTimer.current) window.clearTimeout(sourceTimer.current);
    if (channelTimer.current) window.clearTimeout(channelTimer.current);
  }, []);

  const updateSkippedStep = (stepId, skipped) => {
    const nextSkippedSteps = new Set(onboarding.skippedStepIds || []);
    if (skipped) nextSkippedSteps.add(stepId);
    else nextSkippedSteps.delete(stepId);
    actions.updateOnboarding({ skippedStepIds: Array.from(nextSkippedSteps) });
  };

  const connectSource = () => {
    setSourceError("");
    if (!username.trim() || !password.trim()) {
      setSourceError(t("admin.onboarding.credentialsRequired"));
      return;
    }
    updateSkippedStep("source", false);
    setSourceLoading(true);
    sourceTimer.current = window.setTimeout(() => {
      actions.connectOnboardingSource(selectedSource);
      setSourceLoading(false);
      setSourceDialogOpen(false);
      setExpanded(null);
      setNow(Date.now());
      toast.success(t("admin.onboarding.sourceConnected", { name: selectedSource }), { description: t("admin.onboarding.sourceConnectedDescription") });
    }, 850);
  };

  const addWebsite = () => {
    try {
      const normalized = normalizeWebsite(websiteUrl.trim());
      updateSkippedStep("website", false);
      actions.startOnboardingImport(normalized);
      setWebsiteUrl(normalized);
      setWebsiteError("");
      setExpanded("channel");
      setNow(Date.now());
      toast.success(t("admin.onboarding.websiteAdded"), { description: t("admin.onboarding.websiteAddedDescription") });
    } catch (error) {
      setWebsiteError(t("admin.onboarding.websiteInvalid"));
    }
  };

  const skipWebsite = () => {
    setWebsiteError("");
    updateSkippedStep("website", true);
    setExpanded("channel");
    toast(t("admin.onboarding.websiteSkipped"), { description: t("admin.onboarding.websiteSkippedDescription") });
  };

  const skipChannel = () => {
    updateSkippedStep("channel", true);
    setSelectedChannel(null);
    setChannelDialogOpen(false);
    setChannelPickerMode(false);
    setExpanded("source");
    toast(t("admin.onboarding.channelSkipped"), { description: t("admin.onboarding.channelSkippedDescription") });
  };

  const skipSource = () => {
    updateSkippedStep("source", true);
    setSelectedSource(null);
    setSourceDialogOpen(false);
    setExpanded(null);
    toast(t("admin.onboarding.sourceSkipped"), { description: t("admin.onboarding.sourceSkippedDescription") });
    nav("/today");
  };

  const connectChannel = () => {
    if (!selectedChannel) return;
    updateSkippedStep("channel", false);
    setChannelLoading(true);
    channelTimer.current = window.setTimeout(() => {
      actions.connectOnboardingChannel(selectedChannel);
      setChannelLoading(false);
      setChannelDialogOpen(false);
      setSelectedChannel(null);
      setChannelPickerMode(false);
      setExpanded("source");
      setNow(Date.now());
      toast.success(t("admin.onboarding.channelConnected", { name: selectedChannel.name }), { description: t("admin.onboarding.channelConnectedDescription") });
    }, 900);
  };

  const connectAnotherChannel = () => {
    setExpanded("channel");
    setSelectedChannel(null);
    setChannelPickerMode(true);
    setChannelDialogOpen(true);
  };

  const openConnectorRequest = () => {
    setRequestedConnectorName(search.trim() || onboarding.requestedConnector?.name || "");
    setRequestedConnectorUrl(onboarding.requestedConnector?.vendorUrl || "");
    setConnectorRequestError("");
    setConnectorRequestOpen(true);
  };

  const submitConnectorRequest = () => {
    const name = requestedConnectorName.trim();
    if (!name) {
      setConnectorRequestError(t("admin.onboarding.softwareNameRequired"));
      return;
    }
    actions.updateOnboarding({
      requestedConnector: {
        name,
        vendorUrl: requestedConnectorUrl.trim(),
        requestedAt: new Date().toISOString(),
      },
    });
    setConnectorRequestError("");
    setConnectorRequestOpen(false);
    toast.success(t("admin.onboarding.requestSent"), { description: t("admin.onboarding.requestSentDescription", { name }) });
  };

  const openImportedReview = () => {
    nav("/settings/channels?review=imported");
  };

  const resetOnboarding = () => {
    if (sourceTimer.current) window.clearTimeout(sourceTimer.current);
    if (channelTimer.current) window.clearTimeout(channelTimer.current);
    actions.resetOnboarding();
    setSelectedSource(null);
    setWebsiteUrl("https://vakeclinic.ge");
    setSelectedChannel(null);
    setSearch("");
    setUsername(CONNECTOR_CREDENTIALS.Medesk.username);
    setPassword("askilia-demo-password");
    setSourceDialogOpen(false);
    setChannelDialogOpen(false);
    setChannelPickerMode(false);
    setConnectorRequestOpen(false);
    setRequestedConnectorName("");
    setRequestedConnectorUrl("");
    setConnectorRequestError("");
    setSourceLoading(false);
    setChannelLoading(false);
    setSourceError("");
    setWebsiteError("");
    setExpanded("website");
    setNow(Date.now());
    toast(t("admin.onboarding.restarted"), { description: t("admin.onboarding.restartedDescription") });
  };

  return (
    <div className="min-h-[100dvh] bg-background">
      <header className="border-b border-border bg-surface">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-4 sm:px-6 lg:px-8">
          <IliaMark size={34} className="bg-transparent" />
          <div className="leading-tight">
            <p className="text-[15px] font-semibold tracking-tight text-foreground">Askilia</p>
            <p className="text-xs text-muted-foreground">{localize(state.clinic.workspace)}</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <div className="inline-flex shrink-0 items-center rounded-lg border border-border bg-background p-0.5 shadow-xs" role="group" aria-label={t("language.title")}>
              {[
                { id: "en", label: "EN" },
                { id: "ka", label: "KA" },
              ].map((option) => (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={state.lang === option.id}
                  aria-label={option.id === "en" ? t("language.english") : t("language.georgian")}
                  onClick={() => actions.setLang(option.id)}
                  className={cn(
                    "rounded-md px-2 py-1 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    state.lang === option.id ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <span className="hidden text-xs text-muted-foreground lg:inline">{t("admin.onboarding.savedAutomatically")}</span>
            <Button variant="ghost" size="sm" onClick={() => toast(t("admin.onboarding.supportReady"), { description: t("admin.onboarding.supportReadyDescription") })}>
              <HelpCircle className="h-4 w-4" /> <span className="hidden md:inline">{t("admin.onboarding.getHelp")}</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={resetOnboarding} aria-label={t("admin.onboarding.restartAria")}>
              <RotateCcw className="h-4 w-4" /> <span className="hidden lg:inline">{t("admin.onboarding.restartDemo")}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => nav("/today")} aria-label={t("admin.onboarding.continueLater")}>
              <span className="hidden sm:inline">{t("admin.onboarding.continueLater")}</span><ArrowRight className="h-4 w-4 sm:ml-1.5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-[28px] font-semibold leading-9 tracking-tight text-foreground">{t("admin.onboarding.welcome", { name: firstName })}</h1>
            <p className="mt-1.5 max-w-2xl text-sm leading-6 text-muted-foreground">
              {t("admin.onboarding.intro")}
            </p>
          </div>
          <div className="w-full shrink-0 sm:w-64">
            <div className="mb-2 flex items-center justify-between gap-3 text-xs">
              <span className="font-medium text-foreground">{t("admin.onboarding.reviewedCount", { count: reviewedSteps, total: 3 })}</span>
              <span className="text-muted-foreground">{t("admin.onboarding.duration")}</span>
            </div>
            <Progress value={(reviewedSteps / 3) * 100} className="h-1.5 bg-muted" aria-label={t("admin.onboarding.reviewedAria", { count: reviewedSteps, total: 3 })} />
          </div>
        </div>

        {sourceConnected ? (
          <ImportBanner
            snapshot={importSnapshot}
            services={state.services.length}
            practitioners={practitionerCount}
            rooms={roomCount}
            conversations={connectedConversationCount}
            openPotential={openPotential}
            onReview={openImportedReview}
            onOpenInbox={() => {
              actions.updateOnboarding({ inboxOpenedAt: new Date().toISOString() });
              nav("/inbox");
            }}
            onDashboard={() => nav("/today")}
          />
        ) : null}

        <div className="mt-7 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <section aria-label={t("admin.onboarding.checklistAria")} className="overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
            <SetupTask
              id="website"
              title={t("admin.onboarding.websiteTitle")}
              description={t("admin.onboarding.websiteDescription")}
              state={websiteConnected ? "complete" : websiteSkipped ? "skipped" : "active"}
              optional
              optionalLabel={websiteSkipped ? t("admin.skipped") : t("admin.optional")}
              expanded={expanded === "website"}
              onToggle={() => setExpanded(websiteConnected && expanded === "website" ? null : "website")}
              summary={websiteConnected
                ? t("admin.onboarding.websiteSummary", { url: onboarding.websiteUrl.replace(/^https?:\/\//, "") })
                : websiteSkipped ? t("admin.onboarding.websiteSkippedSummary") : null}
            >
              <div className="space-y-5">
                <div>
                  <Label htmlFor="website-url">{t("admin.onboarding.businessWebsite")}</Label>
                  <p className="mt-1 text-xs text-muted-foreground">{t("admin.onboarding.websiteFound")}</p>
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <div className="relative flex-1">
                      <Globe2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="website-url"
                        value={websiteUrl}
                        onChange={(event) => setWebsiteUrl(event.target.value)}
                        className="pl-9"
                        aria-invalid={Boolean(websiteError)}
                        aria-describedby={websiteError ? "website-url-error" : "website-url-help"}
                      />
                    </div>
                    <Button onClick={addWebsite}>{t("admin.onboarding.importWebsite")} <ArrowRight className="h-4 w-4" /></Button>
                    <Button variant="ghost" onClick={skipWebsite}>{t("admin.skipForNow")}</Button>
                  </div>
                  <p id="website-url-help" className="sr-only">{t("admin.onboarding.websiteHelp")}</p>
                  {websiteError ? <p id="website-url-error" role="alert" className="mt-1.5 text-xs font-medium text-destructive">{websiteError}</p> : null}
                </div>
                {onboarding.knowledgePages?.length ? (
                  <div className="rounded-lg border border-border bg-surface p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs font-semibold text-foreground">{t("admin.onboarding.knowledgePreview")}</p>
                      <Chip tone="muted">{t("admin.onboarding.prototypePreview")}</Chip>
                    </div>
                    <div className="mt-3 divide-y divide-border">
                      {onboarding.knowledgePages.slice(0, 3).map((page) => (
                        <div key={page.id} className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
                          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-foreground">{localize(page.title)}</p>
                            <p className="truncate text-[11px] text-muted-foreground">{page.url}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 text-xs leading-5 text-muted-foreground">{t("admin.onboarding.previewEditHelp")}</p>
                  </div>
                ) : null}
                <div className="grid gap-3 border-t border-border pt-4 sm:grid-cols-2">
                  <KnowledgeSource icon={Database} title={t("admin.onboarding.systemControls")} detail={t("admin.onboarding.systemControlsDetail")} />
                  <KnowledgeSource icon={Globe2} title={t("admin.onboarding.websiteContext")} detail={t("admin.onboarding.websiteContextDetail")} />
                </div>
                <p className="flex items-start gap-2 text-xs leading-5 text-muted-foreground">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                  {t("admin.onboarding.publicPagesOnly")}
                </p>
                <p className="rounded-lg border border-border bg-muted/45 px-3 py-2.5 text-xs leading-5 text-muted-foreground">
                  {t("admin.onboarding.prototypeNote")}
                </p>
              </div>
            </SetupTask>

            <SetupTask
              id="channel"
              title={t("admin.onboarding.channelTitle")}
              description={t("admin.onboarding.channelDescription")}
              state={hasChannel ? "complete" : channelSkipped ? "skipped" : "active"}
              optional
              optionalLabel={channelSkipped ? t("admin.skipped") : t("admin.optional")}
              expanded={expanded === "channel"}
              onToggle={() => setExpanded(hasChannel && expanded === "channel" ? null : "channel")}
              summary={hasChannel
                ? t("admin.onboarding.channelSummary", { channels: onboarding.connectedChannelIds.length, conversations: connectedConversationCount })
                : channelSkipped ? t("admin.onboarding.channelSkippedSummary") : null}
            >
              <div className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-2">
                  {CHANNELS.map((channel) => {
                    const connected = onboarding.connectedChannelIds.includes(channel.id);
                    return (
                      <button
                        key={channel.id}
                        id={`onboarding-${channel.id}`}
                        type="button"
                        disabled={connected}
                        onClick={() => {
                          setChannelPickerMode(false);
                          setSelectedChannel(channel);
                          setChannelDialogOpen(true);
                        }}
                        className={cn(
                          "flex min-h-[72px] items-center gap-3 rounded-lg border p-3.5 text-left transition-[border-color,box-shadow,transform] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          connected
                            ? "border-success/35 bg-success-soft/55"
                            : "border-border bg-background hover:border-primary/40 hover:shadow-xs active:translate-y-px"
                        )}
                      >
                        <span className={cn("inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border bg-surface shadow-xs", connected ? "border-success/30 text-success" : "border-border text-primary")}>
                          {connected ? <Check className="h-5 w-5" /> : <channel.Icon className="h-5 w-5" />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold text-foreground">{channel.name}</span>
                          <span className="mt-0.5 block text-xs text-muted-foreground">{connected ? t("admin.onboarding.connected") : t(channel.descriptionKey)}</span>
                        </span>
                        {!connected ? <ArrowRight className="h-4 w-4 text-muted-foreground" /> : null}
                      </button>
                    );
                  })}
                </div>

                {!hasChannel ? (
                  <div className="flex justify-end border-t border-border pt-4">
                    <Button variant="ghost" onClick={skipChannel}>{t("admin.skipForNow")}</Button>
                  </div>
                ) : null}

                {hasChannel ? (
                  <div className="rounded-lg border border-primary/25 bg-primary-soft/40 p-4">
                    <div className="flex items-start gap-3">
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface text-primary shadow-xs"><MessagesSquare className="h-5 w-5" /></span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground">{t("admin.onboarding.inboxReady")}</p>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground">
                          {connectedConversationCount
                            ? t("admin.onboarding.matchingConversations", { count: connectedConversationCount })
                            : t("admin.onboarding.channelLive")} {t("admin.onboarding.draftMode")}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                      <Button onClick={() => nav("/today")}>{t("admin.onboarding.dashboardAction")} <ArrowRight className="h-4 w-4" /></Button>
                      <Button variant="outline" onClick={() => {
                        actions.updateOnboarding({ inboxOpenedAt: new Date().toISOString() });
                        nav("/inbox");
                      }}>{t("admin.onboarding.openInbox")} <ArrowRight className="h-4 w-4" /></Button>
                      {onboarding.connectedChannelIds.length < CHANNELS.length ? <Button variant="ghost" onClick={connectAnotherChannel}>{t("admin.onboarding.connectAnotherChannel")}</Button> : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </SetupTask>

            <SetupTask
              id="source"
              title={t("admin.onboarding.sourceTitle")}
              description={t("admin.onboarding.sourceDescription")}
              state={sourceConnected ? "complete" : sourceSkipped ? "skipped" : "active"}
              optional
              optionalLabel={sourceSkipped ? t("admin.skipped") : t("admin.optional")}
              expanded={expanded === "source"}
              onToggle={() => setExpanded(sourceConnected && expanded === "source" ? null : "source")}
              summary={sourceConnected
                ? t(onboarding.businessDataReadyAt ? "admin.onboarding.sourceSummaryReady" : "admin.onboarding.sourceSummaryRunning", { name: onboarding.source })
                : sourceSkipped ? t("admin.onboarding.sourceSkippedSummary") : null}
            >
              <div className="space-y-5">
                <div>
                  <Label htmlFor="system-search">{t("admin.onboarding.systemLabel")}</Label>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">{t("admin.onboarding.systemHelp")}</p>
                  <div className="relative mt-2">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input id="system-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("admin.onboarding.searchSystems")} className="pl-9" />
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {filteredSystems.map((system) => (
                    <button
                      key={system.id}
                      type="button"
                      onClick={() => {
                        setSelectedSource(system.id);
                        setUsername(CONNECTOR_CREDENTIALS[system.id].username);
                        setSourceDialogOpen(true);
                      }}
                      className="group flex min-h-[74px] items-center gap-3 rounded-lg border border-border bg-background p-3.5 text-left transition-[border-color,box-shadow,transform] hover:border-primary/40 hover:shadow-xs active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-primary shadow-xs">
                        <system.Icon className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2 text-sm font-semibold text-foreground">{system.id}</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">{t(system.descriptionKey)}</span>
                      </span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                    </button>
                  ))}
                </div>
                {filteredSystems.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border bg-background p-4 text-center">
                    <p className="text-sm font-medium text-foreground">{t("admin.onboarding.noConnector")}</p>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">{t("admin.onboarding.noConnectorHelp")}</p>
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={openConnectorRequest}
                  className="group flex w-full items-center gap-3 rounded-lg border border-dashed border-border bg-surface p-3.5 text-left transition-[border-color,box-shadow,transform] hover:border-primary/40 hover:shadow-xs active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className={cn(
                    "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border bg-background shadow-xs",
                    onboarding.requestedConnector ? "border-success/30 text-success" : "border-border text-primary"
                  )}>
                    {onboarding.requestedConnector ? <Check className="h-4 w-4" /> : <HelpCircle className="h-4 w-4" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-foreground">
                      {onboarding.requestedConnector
                        ? t("admin.onboarding.requestSentFor", { name: onboarding.requestedConnector.name })
                        : t("admin.onboarding.cantSeeSoftware")}
                    </span>
                    <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
                      {onboarding.requestedConnector
                        ? t("admin.onboarding.requestUpdateHelp")
                        : t("admin.onboarding.requestHelp")}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs font-semibold text-primary">{onboarding.requestedConnector ? t("admin.onboarding.update") : t("admin.onboarding.tellUs")}</span>
                </button>
                <div className="flex justify-end border-t border-border pt-4">
                  <Button variant="ghost" onClick={skipSource}>{t("admin.skipForNow")}</Button>
                </div>
              </div>
            </SetupTask>
          </section>

          <ReadinessRail
            onboarding={onboarding}
            snapshot={importSnapshot}
            services={state.services.length}
            practitioners={practitionerCount}
            rooms={roomCount}
            conversations={connectedConversationCount}
            openPotential={openPotential}
          />
        </div>
      </main>

      <Dialog open={connectorRequestOpen} onOpenChange={setConnectorRequestOpen}>
        <DialogContent className="max-w-md overflow-hidden border-border bg-surface p-0 shadow-float" overlayClassName="bg-foreground/25 backdrop-blur-[2px]">
          <DialogHeader className="border-b border-border px-6 py-5 pr-12">
            <DialogTitle>{t("admin.onboarding.requestDialogTitle")}</DialogTitle>
            <DialogDescription>{t("admin.onboarding.requestDialogDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 px-6 py-5">
            <div className="space-y-2">
              <Label htmlFor="requested-connector-name">{t("admin.onboarding.softwareName")}</Label>
              <Input
                id="requested-connector-name"
                value={requestedConnectorName}
                onChange={(event) => setRequestedConnectorName(event.target.value)}
                placeholder={t("admin.onboarding.softwarePlaceholder")}
                autoFocus
                aria-invalid={Boolean(connectorRequestError)}
                aria-describedby={connectorRequestError ? "requested-connector-error" : undefined}
              />
              {connectorRequestError ? <p id="requested-connector-error" role="alert" className="text-xs font-medium text-destructive">{connectorRequestError}</p> : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="requested-connector-url">{t("admin.onboarding.vendorLink")} <span className="font-normal text-muted-foreground">{t("admin.optional")}</span></Label>
              <div className="relative">
                <Globe2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="requested-connector-url"
                  value={requestedConnectorUrl}
                  onChange={(event) => setRequestedConnectorUrl(event.target.value)}
                  placeholder="https://vendor.example"
                  className="pl-9"
                />
              </div>
            </div>
            <p className="text-xs leading-5 text-muted-foreground">{t("admin.onboarding.ownerContactHelp")}</p>
          </div>
          <DialogFooter className="border-t border-border bg-background px-6 py-4">
            <Button variant="outline" onClick={() => setConnectorRequestOpen(false)}>{t("admin.cancel")}</Button>
            <Button onClick={submitConnectorRequest}>{t("admin.onboarding.sendRequest")} <ArrowRight className="h-4 w-4" /></Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sourceDialogOpen} onOpenChange={(open) => !sourceLoading && setSourceDialogOpen(open)}>
        <DialogContent className="max-w-xl overflow-hidden border-border bg-surface p-0 shadow-float" overlayClassName="bg-foreground/25 backdrop-blur-[2px]">
          <DialogHeader className="border-b border-border px-6 py-5 pr-12">
            <DialogTitle>{t("admin.onboarding.connectSystemDialog", { name: selectedSource || t("admin.onboarding.yourClinicSystem") })}</DialogTitle>
            <DialogDescription>{t("admin.onboarding.reviewAccess")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 px-6 py-5">
            <div className="rounded-lg border border-primary/20 bg-primary-soft/35 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-foreground"><LockKeyhole className="h-4 w-4 text-primary" /> {t("admin.onboarding.readOnly")}</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {PERMISSIONS.map((permission) => (
                  <p key={permission} className="flex items-start gap-2 text-xs leading-5 text-muted-foreground"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" /> {t(permission)}</p>
                ))}
              </div>
              <p className="mt-3 border-t border-primary/15 pt-3 text-xs text-primary-soft-foreground">{t("admin.onboarding.noClinicalFiles")}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="connector-username">{t("admin.onboarding.username")}</Label>
                <Input
                  id="connector-username"
                  type="text"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="username"
                  aria-invalid={Boolean(sourceError)}
                  aria-describedby={sourceError ? "source-credentials-error" : "source-credentials-help"}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="connector-password">{t("admin.onboarding.password")}</Label>
                <Input
                  id="connector-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  aria-invalid={Boolean(sourceError)}
                  aria-describedby={sourceError ? "source-credentials-error" : "source-credentials-help"}
                />
              </div>
            </div>
            {sourceError ? <p id="source-credentials-error" role="alert" className="text-xs font-medium text-destructive">{sourceError}</p> : null}
            <p id="source-credentials-help" className="text-xs text-muted-foreground">{t("admin.onboarding.credentialsHelp")}</p>
          </div>
          <DialogFooter className="border-t border-border bg-background px-6 py-4">
            <Button variant="outline" onClick={() => setSourceDialogOpen(false)} disabled={sourceLoading}>{t("admin.cancel")}</Button>
            <Button onClick={connectSource} disabled={sourceLoading}>
              {sourceLoading ? <><LoaderCircle className="h-4 w-4 animate-spin" /> {t("admin.onboarding.authenticating")}</> : <>{t("admin.onboarding.allowReadOnly")} <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={channelDialogOpen} onOpenChange={(open) => {
        if (channelLoading) return;
        setChannelDialogOpen(open);
        if (!open) {
          setSelectedChannel(null);
          setChannelPickerMode(false);
        }
      }}>
        <DialogContent className="max-w-md overflow-hidden border-border bg-surface p-0 shadow-float" overlayClassName="bg-foreground/25 backdrop-blur-[2px]">
          <DialogHeader className="border-b border-border px-6 py-5 pr-12">
            <DialogTitle>{selectedChannel ? t("admin.onboarding.connectChannelNamed", { name: selectedChannel.name }) : t("admin.onboarding.connectAnotherChannel")}</DialogTitle>
            <DialogDescription>
              {selectedChannel
                ? t("admin.onboarding.channelDialogDescription")
                : t("admin.onboarding.channelPickerDescription")}
            </DialogDescription>
          </DialogHeader>
          {selectedChannel ? (
            <>
              <div className="space-y-4 px-6 py-5">
                <div className="flex items-center gap-3 rounded-lg border border-border bg-background p-4">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-surface text-primary shadow-xs">
                    <selectedChannel.Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{selectedChannel.handle}</p>
                    <p className="text-xs text-muted-foreground">{t("admin.onboarding.historySync")}</p>
                  </div>
                </div>
                <p className="flex items-start gap-2 text-xs leading-5 text-muted-foreground"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" /> {t("admin.onboarding.noAutonomousReplies")}</p>
                <p className="text-xs text-muted-foreground">{t("admin.onboarding.prototypeChannelHelp")}</p>
              </div>
              <DialogFooter className="border-t border-border bg-background px-6 py-4">
                {channelPickerMode ? <Button variant="ghost" onClick={() => setSelectedChannel(null)} disabled={channelLoading}>{t("admin.back")}</Button> : null}
                <Button variant="outline" onClick={() => setChannelDialogOpen(false)} disabled={channelLoading}>{t("admin.cancel")}</Button>
                <Button onClick={connectChannel} disabled={channelLoading}>
                  {channelLoading ? <><LoaderCircle className="h-4 w-4 animate-spin" /> {t("admin.onboarding.connecting")}</> : <>{t("admin.onboarding.connectChannel")} <ArrowRight className="h-4 w-4" /></>}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-2 px-6 py-5">
                {remainingChannels.map((channel) => (
                  <button
                    key={channel.id}
                    type="button"
                    onClick={() => setSelectedChannel(channel)}
                    className="group flex w-full items-center gap-3 rounded-lg border border-border bg-background p-3.5 text-left transition-[border-color,box-shadow,transform] hover:border-primary/40 hover:shadow-xs active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-primary shadow-xs"><channel.Icon className="h-5 w-5" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-foreground">{channel.name}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{t(channel.descriptionKey)}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                  </button>
                ))}
              </div>
              <DialogFooter className="border-t border-border bg-background px-6 py-4">
                <Button variant="outline" onClick={() => setChannelDialogOpen(false)}>{t("admin.cancel")}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SetupTask({ id, title, description, state, expanded, onToggle, summary, optional = false, optionalLabel, children }) {
  const { t } = useI18n();
  const locked = state === "locked";
  const complete = state === "complete";
  const current = expanded && !complete;
  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        disabled={locked}
        aria-expanded={expanded}
        aria-controls={`${id}-content`}
        className={cn(
          "flex min-h-[76px] w-full items-center gap-3 px-5 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed sm:px-6",
          current && "cursor-default bg-primary-soft/25"
        )}
      >
        <span className={cn(
          "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border",
          complete
            ? "border-success bg-success text-success-foreground"
            : locked
              ? "border-border bg-muted text-muted-foreground"
              : "border-primary bg-primary-soft text-primary"
        )}>
          {complete ? <Check className="h-4 w-4" /> : locked ? <LockKeyhole className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5 fill-current" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[15px] font-semibold leading-5 text-foreground">{title}</span>
            {optional ? <span className="text-[11px] font-medium text-muted-foreground">{optionalLabel || t("admin.optional")}</span> : null}
          </span>
          <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{summary || description}</span>
        </span>
        {!locked ? (
          current
            ? <span className="max-w-28 shrink-0 text-right text-xs font-semibold leading-4 text-primary">{t("admin.onboarding.continueHere")}</span>
            : <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", expanded && "rotate-180")} />
        ) : null}
      </button>
      {expanded && !locked ? <div id={`${id}-content`} className="border-t border-border bg-background/55 px-5 py-5 sm:px-6 sm:py-6">{children}</div> : null}
    </div>
  );
}

function KnowledgeSource({ icon: Icon, title, detail }) {
  return (
    <div className="flex items-start gap-3">
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-primary shadow-xs"><Icon className="h-4 w-4" /></span>
      <div><p className="text-xs font-semibold text-foreground">{title}</p><p className="mt-0.5 text-xs leading-5 text-muted-foreground">{detail}</p></div>
    </div>
  );
}

function ImportBanner({ snapshot, services, practitioners, rooms, conversations, openPotential, onReview, onOpenInbox, onDashboard }) {
  const reduceMotion = useReducedMotion();
  const { t, formatMoney } = useI18n();

  if (snapshot.ready) {
    return (
      <section className="mt-7 overflow-hidden rounded-xl border border-success/35 bg-success-soft/65 shadow-xs animate-fade-rise" aria-labelledby="business-ready-title">
        <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:p-6">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-surface text-success shadow-xs"><CheckCircle2 className="h-6 w-6" /></span>
          <div className="min-w-0 flex-1">
            <h2 id="business-ready-title" className="text-base font-semibold text-foreground">{t("admin.onboarding.businessReady")}</h2>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">{t("admin.onboarding.businessReadySummary", { services, practitioners, rooms, conversations })}</p>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm font-semibold text-success-soft-foreground">{t("admin.onboarding.openValueFound", { value: formatMoney(openPotential) })} <SourceTag source="derived" /></p>
          </div>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
            <Button onClick={onReview}>{t("admin.onboarding.reviewBusinessData")} <ArrowRight className="h-4 w-4" /></Button>
            <Button variant="outline" onClick={onOpenInbox}>{t("admin.onboarding.openInbox")}</Button>
            <Button variant="ghost" onClick={onDashboard}>{t("admin.onboarding.dashboard")}</Button>
          </div>
        </div>
      </section>
    );
  }

  const primaryText = snapshot.blockedBy === "website"
    ? t("admin.onboarding.waitingWebsite")
    : snapshot.blockedBy === "channel"
      ? t("admin.onboarding.waitingChannel")
      : (snapshot.activeTask?.activeKey ? t(snapshot.activeTask.activeKey, snapshot.activeTask.params) : snapshot.activeTask?.activeLabel) || t("admin.onboarding.preparingWorkspace");
  const ActiveIcon = IMPORT_TASK_ICONS[snapshot.activeTask?.id] || Sparkles;
  const stageMotion = reduceMotion ? false : { opacity: 0, y: 4 };
  const stageExit = reduceMotion ? undefined : { opacity: 0, y: -3 };

  return (
    <section className="mt-7 rounded-xl border border-primary/25 bg-surface p-5 shadow-xs sm:p-6" aria-labelledby="import-title">
      <div className="flex items-start gap-3.5">
        <motion.div
          key={snapshot.activeTask?.id || "preparing"}
          initial={reduceMotion ? false : { opacity: 0.65, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 330, damping: 24 }}
          className="relative h-11 w-11 shrink-0"
          aria-hidden="true"
        >
          <IliaAvatar size={42} active surface={false} />
          <span className="absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-border bg-surface text-primary shadow-xs">
            <ActiveIcon className="h-3 w-3" />
          </span>
          <motion.span
            initial={reduceMotion ? false : { opacity: 0, scale: 0.6, rotate: -12 }}
            animate={{ opacity: [0, 1, 0], scale: [0.6, 1, 0.92], rotate: [-12, 0, 8] }}
            transition={{ duration: reduceMotion ? 0 : 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="absolute -right-2 -top-2 text-ilia"
          >
            <Sparkles className="h-4 w-4" />
          </motion.span>
        </motion.div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h2 id="import-title" className="text-sm font-semibold text-foreground">{t("admin.onboarding.iliaPreparing")}</h2>
              <AnimatePresence initial={false} mode="wait">
                <motion.p
                  key={primaryText}
                  initial={stageMotion}
                  animate={{ opacity: 1, y: 0 }}
                  exit={stageExit}
                  transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
                  className="mt-0.5 text-sm text-muted-foreground"
                >
                  {primaryText}
                </motion.p>
              </AnimatePresence>
              <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">{primaryText}</p>
            </div>
            <span className="shrink-0 text-xs font-medium text-primary">{t("admin.onboarding.dataTasksReady", { count: snapshot.completedCount, total: ONBOARDING_IMPORT_TASKS.length })}</span>
          </div>
          <Progress value={snapshot.progress} className="mt-4 h-1.5 bg-primary-soft" aria-label={t("admin.onboarding.dataTasksReady", { count: snapshot.completedCount, total: ONBOARDING_IMPORT_TASKS.length })} />
          <div className="mt-2 flex min-h-5 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="min-h-5 overflow-hidden" aria-hidden="true">
              <AnimatePresence initial={false} mode="wait">
                {snapshot.activeMessage ? (
                  <motion.p
                    key={snapshot.activeMessage}
                    initial={stageMotion}
                    animate={{ opacity: 1, y: 0 }}
                    exit={stageExit}
                    transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                    className="text-xs leading-5 text-muted-foreground"
                  >
                    {snapshot.activeMessageKey ? t(snapshot.activeMessageKey, snapshot.activeMessageParams) : snapshot.activeMessage}
                  </motion.p>
                ) : null}
              </AnimatePresence>
            </div>
            <AnimatePresence initial={false} mode="wait">
              {snapshot.latestCompletedTask ? (
                <motion.p
                  key={snapshot.latestCompletedTask.id}
                  initial={stageMotion}
                  animate={{ opacity: 1, y: 0 }}
                  exit={stageExit}
                  transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
                  className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-success-soft-foreground"
                >
                  <Check className="h-3.5 w-3.5" /> {snapshot.latestCompletedTask.labelKey ? t(snapshot.latestCompletedTask.labelKey, snapshot.latestCompletedTask.params) : snapshot.latestCompletedTask.label}
                </motion.p>
              ) : null}
            </AnimatePresence>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{t("admin.onboarding.continueSetupHelp")}</p>
        </div>
      </div>
    </section>
  );
}

function ReadinessRail({ onboarding, snapshot, services, practitioners, rooms, conversations, openPotential }) {
  const { t, formatMoney } = useI18n();
  const taskDone = (id) => {
    return snapshot.ready || snapshot.completedTaskIds.includes(id);
  };
  const publicKnowledgeReady = Boolean(onboarding.websiteUrl && onboarding.knowledgePages?.length);
  const conversationsReady = Boolean(onboarding.connectedChannelIds?.length);

  const items = [
    {
      id: "services",
      icon: Database,
      title: t("admin.onboarding.readinessServices"),
      ready: taskDone("services"),
      value: t("admin.onboarding.readinessServicesReady", { services, practitioners, rooms }),
      pending: onboarding.sourceConnectedAt ? t("admin.onboarding.importingServices") : t("admin.onboarding.connectSystemWhenReady"),
    },
    {
      id: "knowledge",
      icon: Globe2,
      title: t("admin.onboarding.readinessKnowledge"),
      ready: publicKnowledgeReady || taskDone("knowledge"),
      value: t("admin.onboarding.readinessKnowledgeReady", { count: onboarding.knowledgePages?.length || 0 }),
      pending: onboarding.websiteUrl ? t("admin.onboarding.preparingKnowledge") : t("admin.onboarding.addWebsiteIfAny"),
    },
    {
      id: "conversations",
      icon: MessagesSquare,
      title: t("admin.onboarding.readinessConversations"),
      ready: conversationsReady || taskDone("conversations"),
      value: conversations ? t("admin.onboarding.matchingReady", { count: conversations }) : t("admin.onboarding.channelReadyNew"),
      pending: t("admin.onboarding.connectChannelToBegin"),
    },
  ];

  return (
    <aside className="space-y-4 lg:sticky lg:top-6" aria-label={t("admin.onboarding.preparingAria")}>
      <div className="rounded-xl border border-border bg-surface p-5 shadow-xs">
        <div className="flex items-center gap-2.5">
          <Sparkles className="h-4 w-4 text-ilia" />
          <h2 className="text-sm font-semibold text-foreground">{t("admin.onboarding.preparingTitle")}</h2>
        </div>
        <div className="mt-4 space-y-4">
          {items.map((item) => (
            <div key={item.id} className="flex items-start gap-3">
              <span className={cn("inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border", item.ready ? "border-success/25 bg-success-soft text-success" : "border-border bg-background text-muted-foreground")}>
                {item.ready ? <Check className="h-4 w-4" /> : <item.icon className="h-4 w-4" />}
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground">{item.title}</p>
                <p className="mt-0.5 text-xs leading-5 text-muted-foreground">{item.ready ? item.value : item.pending}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {conversationsReady && openPotential > 0 ? (
        <div className="rounded-xl border border-primary/25 bg-primary-soft/35 p-5 shadow-xs animate-fade-rise">
          <p className="text-xs font-semibold text-primary-soft-foreground">{t("admin.onboarding.commercialContext")}</p>
          <p className="mt-2 text-2xl font-semibold tracking-tight text-foreground">{formatMoney(openPotential)}</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{t("admin.onboarding.commercialContextDescription")}</p>
          <SourceTag source="derived" className="mt-3" />
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-surface p-5 shadow-xs">
          <p className="text-xs font-semibold text-foreground">{conversationsReady ? t("admin.onboarding.inboxReadyShort") : t("admin.onboarding.whyChannel")}</p>
          <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
            {conversationsReady
              ? t("admin.onboarding.noSampleHistory")
              : t("admin.onboarding.whyChannelDescription")}
          </p>
        </div>
      )}
    </aside>
  );
}
